import { useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { calculateObliqueMatrix } from "../utils/projection";
import type { ProjectionParams } from "../config/types";

/**
 * 각 코너별 클리핑 설정
 */
export interface CornerClippingConfig {
  enabled: boolean;
  controlPoint: number;
  hypotenuseOffset: number; // 빗변 오프셋 (애니메이션용)
}

/**
 * GUI 클리핑 매니저
 * 씬에 있는 모든 건물 머티리얼의 GUI 클리핑 uniforms를 업데이트
 */
interface GUIClippingManagerProps {
  enabled: boolean;
  projectionParams: ProjectionParams;
  getPanOffset: () => THREE.Vector3;
  debug?: boolean;
  // 각 코너별 설정
  topLeft: CornerClippingConfig;
  topRight: CornerClippingConfig;
  bottomLeft: CornerClippingConfig;
  bottomRight: CornerClippingConfig;
}

export function GUIClippingManager({
  enabled,
  projectionParams,
  getPanOffset,
  debug = false,
  topLeft,
  topRight,
  bottomLeft,
  bottomRight,
}: GUIClippingManagerProps) {
  const { scene, camera } = useThree();
  const vpMatrixRef = useRef(new THREE.Matrix4());
  const tempMatrix = useRef(new THREE.Matrix4());
  const worldXDir = useRef(new THREE.Vector2(0.866, -0.5));
  const worldZDir = useRef(new THREE.Vector2(-0.866, -0.5));

  // 매 프레임마다 업데이트
  useFrame(() => {
    if (!enabled) return;

    // VP 행렬 계산 (oblique projection 포함)
    const panOffset = getPanOffset();
    const obliqueMatrix = calculateObliqueMatrix(projectionParams, panOffset);

    tempMatrix.current
      .copy(camera.projectionMatrix)
      .multiply(camera.matrixWorldInverse)
      .multiply(obliqueMatrix);
    vpMatrixRef.current.copy(tempMatrix.current);

    // 월드 축 방향 계산 (NDC로 투영)
    const origin4 = new THREE.Vector4(0, 0, 0, 1).applyMatrix4(
      vpMatrixRef.current
    );
    const xAxis4 = new THREE.Vector4(1, 0, 0, 1).applyMatrix4(
      vpMatrixRef.current
    );
    const zAxis4 = new THREE.Vector4(0, 0, 1, 1).applyMatrix4(
      vpMatrixRef.current
    );

    const originNDC = new THREE.Vector2(
      origin4.x / origin4.w,
      origin4.y / origin4.w
    );
    const xNDC = new THREE.Vector2(xAxis4.x / xAxis4.w, xAxis4.y / xAxis4.w);
    const zNDC = new THREE.Vector2(zAxis4.x / zAxis4.w, zAxis4.y / zAxis4.w);

    worldXDir.current.copy(xNDC).sub(originNDC).normalize();
    worldZDir.current.copy(zNDC).sub(originNDC).normalize();

    // 씬 순회하여 건물/엣지 셰이더 머티리얼 업데이트
    scene.traverse((object) => {
      // Mesh 또는 LineSegments에서 ShaderMaterial 처리
      const material = (object as THREE.Mesh | THREE.LineSegments)
        .material as THREE.ShaderMaterial;

      if (!material?.uniforms) return;

      // GUI 클리핑 uniforms가 있는 머티리얼인지 확인
      if (material.uniforms.uGUIControlPoint) {
        // 월드 축 방향 업데이트
        material.uniforms.uWorldXDir.value.copy(worldXDir.current);
        material.uniforms.uWorldZDir.value.copy(worldZDir.current);
        material.uniforms.uEnableGUIClipping.value = 1.0;

        // 디버그 모드 설정
        if (material.uniforms.uGUIClippingDebug) {
          material.uniforms.uGUIClippingDebug.value = debug ? 1.0 : 0.0;
        }

        // 각 코너별 설정
        if (material.uniforms.uTopLeftEnabled) {
          material.uniforms.uTopLeftEnabled.value = topLeft.enabled ? 1.0 : 0.0;
        }
        if (material.uniforms.uTopRightEnabled) {
          material.uniforms.uTopRightEnabled.value = topRight.enabled
            ? 1.0
            : 0.0;
        }
        if (material.uniforms.uBottomLeftEnabled) {
          material.uniforms.uBottomLeftEnabled.value = bottomLeft.enabled
            ? 1.0
            : 0.0;
        }
        if (material.uniforms.uBottomRightEnabled) {
          material.uniforms.uBottomRightEnabled.value = bottomRight.enabled
            ? 1.0
            : 0.0;
        }

        // 각 코너별 controlPoint 업데이트
        if (material.uniforms.uTopLeftControlPoint) {
          material.uniforms.uTopLeftControlPoint.value = topLeft.controlPoint;
        }
        if (material.uniforms.uTopRightControlPoint) {
          material.uniforms.uTopRightControlPoint.value = topRight.controlPoint;
        }
        if (material.uniforms.uBottomLeftControlPoint) {
          material.uniforms.uBottomLeftControlPoint.value =
            bottomLeft.controlPoint;
        }
        if (material.uniforms.uBottomRightControlPoint) {
          material.uniforms.uBottomRightControlPoint.value =
            bottomRight.controlPoint;
        }

        // 각 코너별 hypotenuseOffset 업데이트 (애니메이션)
        if (material.uniforms.uTopLeftOffset) {
          material.uniforms.uTopLeftOffset.value = topLeft.hypotenuseOffset;
        }
        if (material.uniforms.uTopRightOffset) {
          material.uniforms.uTopRightOffset.value = topRight.hypotenuseOffset;
        }
        if (material.uniforms.uBottomLeftOffset) {
          material.uniforms.uBottomLeftOffset.value =
            bottomLeft.hypotenuseOffset;
        }
        if (material.uniforms.uBottomRightOffset) {
          material.uniforms.uBottomRightOffset.value =
            bottomRight.hypotenuseOffset;
        }

        // 기존 단일 controlPoint도 업데이트 (하위 호환성)
        material.uniforms.uGUIControlPoint.value = topLeft.controlPoint;
      }
    });
  });

  return null;
}
